import type { PlaceId, EventId } from "./ids";
import { serverTimeMs } from "./server-time";

export const LIVE_CONFIRMATIONS = ["CONFIRM", "DISPUTE", "OUTDATED"] as const;
export type LiveConfirmation = (typeof LIVE_CONFIRMATIONS)[number];

/** live_service.LIVE_TYPES 실측 7종. */
export const LIVE_TYPES = [
  "WAIT_TIME", "SOLD_OUT", "RESTOCK", "BENEFIT_ENDED",
  "BOOKING_CLOSED", "TEMP_CLOSED", "NORMAL",
] as const;
export type LiveType = (typeof LIVE_TYPES)[number];

export const LIVE_STATUSES = ["ACTIVE", "EXPIRED", "WITHDRAWN"] as const;
export type LiveStatus = (typeof LIVE_STATUSES)[number];

/** DEFAULT_TTL 실측 (시간 단위). */
export const LIVE_TTL_HOURS: Readonly<Record<LiveType, number>> = {
  WAIT_TIME: 2, SOLD_OUT: 12, RESTOCK: 12, BENEFIT_ENDED: 12,
  BOOKING_CLOSED: 12, TEMP_CLOSED: 24, NORMAL: 24,
};

export interface LiveUpdate {
  id: string;
  placeId: PlaceId | null;
  eventId: EventId | null;
  reporterId: string | null;
  type: LiveType | string;
  value: Record<string, unknown>;
  status: LiveStatus | string;
  observedAt: string;
  expiresAt: string;
  confirmedCount: number;
}

/**
 * I-LU-02 + AC-07 잔여 구멍 대응.
 * 서버 만료는 /live-updates 조회 시점에만 반영된다. /map/items 와 /places/<id> 는 반영하지 않으므로
 * 클라이언트가 표시 시점에 한 번 더 거른다.
 */
export function isLiveVisible(u: Pick<LiveUpdate, "status" | "expiresAt">, now: Date = new Date()): boolean {
  if (u.status !== "ACTIVE") return false;
  const t = serverTimeMs(u.expiresAt);
  return Number.isFinite(t) && t > now.getTime();
}

export function isLiveType(v: string): v is LiveType {
  return (LIVE_TYPES as readonly string[]).includes(v);
}

/**
 * 관측 시각이 얼마나 지났는지를 신선도로 환산한다 (1 = 방금, 0 = 다 됨).
 *
 * 서버가 만료시키기 전이라도 **거의 다 된 정보는 그렇게 보여야 한다** (P4).
 * 대기시간은 2시간이면 이미 옛말이고 임시휴업은 하루쯤 유효하다 —
 * 유형마다 유효 기간이 다른 것이 곧 신뢰의 차이다.
 */
export function liveFreshness(
  u: Pick<LiveUpdate, "type" | "observedAt">,
  now: Date = new Date(),
): number {
  const t = serverTimeMs(u.observedAt);
  if (!Number.isFinite(t)) return 0;
  const ttlHours = isLiveType(u.type) ? LIVE_TTL_HOURS[u.type] : 12;
  const age = now.getTime() - t;
  if (age <= 0) return 1;
  return Math.max(0, Math.min(1, 1 - age / (ttlHours * 3600_000)));
}
